import { ERentioFormField } from "@rentiohq/shared-frontend/dist/components/components/Form";
import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import {
  IUiSchema,
  IUiSchemaHelpInfoField,
} from "@rentiohq/shared-frontend/dist/forms/forms.types";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { getValue } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { setAddressFieldsError } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { subYears } from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { getMappedNationalities } from "@rentiohq/shared-frontend/dist/utils/location/location.utils";
import utils from "@rentiohq/web-shared/dist/utils";
import { JSONSchema6 } from "json-schema";
import { FormValidation } from "react-native-jsonschema-form-test";
import { EField, IExtraData } from "./schema.kycVerification.types";

const { i18n } = utils;

const NOW = new Date();

// The dropdown's `options` is the form framework's extension of a JSON schema field, so the fields are typed
// with it instead of suppressing the error (same approach as TAutoRegistrationField in
// schema.createDocumentPackage.7.ts)
type TKycSchemaField = JSONSchema6 & {
  options?: ReturnType<typeof getMappedNationalities>;
};

const kycFields = (fields: { [key: string]: TKycSchemaField }) => fields;

const legalUserSchema = (
  initialValues: { [key: string]: any },
  defaultValues: { [key: string]: any },
): JSONSchema6 => ({
  required: [EField.DateOfBirth, EField.Nationality, EField.Address],
  // The banners first, the representative's details, the document route as the fallback last
  properties: kycFields({
    [EField.KybWhyInfo]: { default: undefined },
    [EField.KybPrepareInfo]: { default: undefined },
    [EField.KybRepresentativeInfo]: { default: undefined },
    [EField.DateOfBirth]: {
      default: getValue(EField.DateOfBirth, initialValues, defaultValues),
    },
    [EField.Nationality]: {
      default: getValue(EField.Nationality, initialValues, defaultValues),
      options: getMappedNationalities(),
    },
    [EField.Address]: {
      default: getValue(EField.Address, initialValues, defaultValues),
    },
    [EField.KybDocumentsInfo]: { default: undefined },
  }),
});

const naturalUserSchema = (
  initialValues: { [key: string]: any },
  defaultValues: { [key: string]: any },
): JSONSchema6 => ({
  required: [
    EField.DateOfBirth,
    EField.Nationality,
    EField.Address,
    EField.IdCardFront,
    EField.IdCardBack,
  ],
  properties: kycFields({
    [EField.DateOfBirth]: {
      default: getValue(EField.DateOfBirth, initialValues, defaultValues),
    },
    [EField.Nationality]: {
      default: getValue(EField.Nationality, initialValues, defaultValues),
      options: getMappedNationalities(),
    },
    [EField.Address]: {
      default: getValue(EField.Address, initialValues, defaultValues),
    },
    [EField.IdCardInfo]: {
      default: undefined,
    },
    [EField.IdCardFront]: {
      default: getValue(EField.IdCardFront, initialValues, defaultValues),
    },
    [EField.IdCardBack]: {
      default: getValue(EField.IdCardBack, initialValues, defaultValues),
    },
  }),
});

export const getSchema =
  (initialValues: { [key: string]: any }, extraData: IExtraData) =>
  (defaultValues: { [key: string]: any }): JSONSchema6 =>
    extraData.isLegalUser
      ? legalUserSchema(initialValues, defaultValues)
      : naturalUserSchema(initialValues, defaultValues);

// The company name lands in Markdown banners: escape what Markdown would read as syntax (links, emphasis, HTML entities)
const escapeMarkdown = (value: string) =>
  value.replace(/[\\`*_~{}[\]()#+\-.!|<>&]/g, "\\$&");

// DEV-2801: the guidance of the KYB mail sent to offices on 2026-09-25, per step
const kybBanner = (
  key: string,
  values: { [key: string]: any } = {},
): IUiSchemaHelpInfoField<EField> => ({
  "ui:field": ERentioFormField.HelpInfo,
  title: getLocalizedText(`form.kyb_receiver_as_broker.banner.${key}.title`),
  subtitle: getLocalizedText(
    `form.kyb_receiver_as_broker.banner.${key}.content`,
    values,
  ),
  subtitleIsMarkdown: true,
});

const legalUserUiSchema = (extraData: IExtraData): IUiSchema<EField> => {
  const companyName =
    extraData.kybInfo?.companyName || getName(extraData.contact);
  // An office's account holder is the representative, not the company: the address is theirs
  const isCompanyContact = extraData.kybInfo?.isCompanyContact ?? true;

  return {
    "rentio:navigationTitle": getLocalizedText(
      "form.kyb_receiver_as_broker.title",
      { name: companyName },
    ),
    [EField.KybWhyInfo]: kybBanner("why", {
      name: escapeMarkdown(companyName),
    }),
    [EField.KybPrepareInfo]: kybBanner("prepare"),
    [EField.KybRepresentativeInfo]: kybBanner("representative"),
    [EField.DateOfBirth]: {
      "ui:field": ERentioFormField.Date,
      title: getLocalizedText(
        "form.kyb_receiver_as_broker.field.date_of_birth",
      ),
      minDate: subYears(NOW, CONFIG.CONTACT_AGE_RANGE.MIN),
      maxDate: subYears(NOW, CONFIG.CONTACT_AGE_RANGE.MAX),
      formatSummary: formData => (formData ? formatDate(formData) : undefined),
    },
    [EField.Nationality]: {
      "ui:field": ERentioFormField.DropdownSelect,
      title: getLocalizedText("form.kyb_receiver_as_broker.field.nationality"),
    },
    [EField.Address]: {
      "ui:field": ERentioFormField.Address,
      title: getLocalizedText(
        isCompanyContact
          ? "form.kyb_receiver_as_broker.field.address"
          : "form.kyb_receiver_as_broker.field.address_representative",
      ),
    },
    [EField.KybDocumentsInfo]: kybBanner("documents"),
  };
};

const naturalUserUiSchema = (extraData: IExtraData): IUiSchema<EField> => ({
  "rentio:navigationTitle": getLocalizedText(
    "form.kyc_receiver_as_broker.title",
    {
      name: getName(extraData.contact),
    },
  ),
  [EField.Address]: {
    "ui:field": ERentioFormField.Address,
    title: getLocalizedText("system.address"),
  },
  [EField.DateOfBirth]: {
    "ui:field": ERentioFormField.Date,
    title: getLocalizedText("system.date_of_birth"),
    minDate: subYears(NOW, CONFIG.CONTACT_AGE_RANGE.MIN),
    maxDate: subYears(NOW, CONFIG.CONTACT_AGE_RANGE.MAX),
    formatSummary: formData => (formData ? formatDate(formData) : undefined),
  },
  [EField.Nationality]: {
    "ui:field": ERentioFormField.DropdownSelect,
    title: getLocalizedText("system.nationality"),
  },
  [EField.IdCardInfo]: {
    "ui:field": ERentioFormField.HelpInfo,
    title: getLocalizedText("form.kyc_receiver_as_broker.id_card_info.title"),
    subtitle: i18n.t("form.kyc_receiver_as_broker.id_card_info.details"),
  },
  [EField.IdCardFront]: {
    "ui:field": ERentioFormField.DocumentSelectInline,
    title: getLocalizedText(
      "form.kyc_receiver_as_broker.field.id_card.front.info.title",
    ),
    accept: ["application/pdf", "image/jpeg", "image/png"],
    multiple: false,
    minFileSize: 32_000, // 32KB
    maxFileSize: 10_000_000, // 10MB
    fileName: getLocalizedText(
      "form.kyc_receiver_as_broker.field.id_card.front.info.title",
    ),
  },
  [EField.IdCardBack]: {
    "ui:field": ERentioFormField.DocumentSelectInline,
    title: getLocalizedText(
      "form.kyc_receiver_as_broker.field.id_card.back.info.title",
    ),
    accept: ["application/pdf", "image/jpeg", "image/png"],
    multiple: false,
    minFileSize: 32_000, // 32KB
    maxFileSize: 10_000_000, // 10MB
    fileName: getLocalizedText(
      "form.kyc_receiver_as_broker.field.id_card.back.info.title",
    ),
  },
});

export const uiSchema = (extraData: IExtraData): IUiSchema<EField> =>
  extraData.isLegalUser
    ? legalUserUiSchema(extraData)
    : naturalUserUiSchema(extraData);

export const getValidate =
  (extraData: IExtraData) => (formData: any, errors: FormValidation) => {
    const addressFormData = formData[EField.Address];

    if (addressFormData) {
      setAddressFieldsError(addressFormData, errors, EField.Address);
    }

    return errors;
  };
